// Bulk-edit modal for the Quick Edit Sitemap tab. Appears when the user has selected
// multiple pages and clicks "Bulk Edit". Lets them add tags and/or change the post status
// across every selected page. Only the fields the user actually sets are applied — the
// confirmation + WordPress writes happen in App (handleSitemapBulkEdit).

import { useState, type CSSProperties } from "react";
import ParentSelect from "./ParentSelect";

export interface BulkEditValues {
  tags: string[];         // tag names to apply (merged with existing tags unless overwriteTags)
  overwriteTags: boolean; // true → replace each page's tags with `tags`; false → merge (no dupes)
  status: string;         // "" = no change, else publish | private | pending | draft
  parent: string;         // "" = no change, "0" = top level (no parent), else a parent page id
  urls: string;           // "Edit Custom URLs" variant — pasted URLs (one per line) to match & edit
  slug: string;           // "Parent Page Edit" variant — new URL slug for the single page ("" = no change)
  redirect: string;       // "Edit Page URL" variant — 301 redirect destination ("" = none / remove existing)
  manageRedirect: boolean;// true when the Redirect Page field was shown (Edit Page URL flow) → App add/updates/removes it
}

interface Props {
  count: number;
  // Candidate parent pages: id + a display label (the URL path after the domain).
  pages: { id: number; label: string }[];
  theme: "light" | "dark";
  saving: boolean;
  onClose: () => void;
  onApply: (values: BulkEditValues) => void;
  // Optional overrides so the same dialog can act as a focused "Parent Page Edit".
  title?: string;         // header (default "Bulk Edit")
  intro?: string;         // replaces the default "Applying to N…" sentence
  showTags?: boolean;     // default true
  showStatus?: boolean;   // default true
  showUrls?: boolean;     // "Edit Custom URLs" variant — show a paste-URLs textarea (default false)
  showSlug?: boolean;     // "Parent Page Edit" variant — show a URL-slug input (default false)
  initialSlug?: string;   // the page's current slug, pre-filled into the slug input
  initialRedirect?: string; // the page's current redirect destination, pre-filled into the Redirect Page input
  parentDisabled?: boolean; // grey out the Parent Page selector (e.g. the edited row is a Post)
}

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "— No change —", value: "" },
  { label: "Published", value: "publish" },
  { label: "Private", value: "private" },
  { label: "Pending Review", value: "pending" },
  { label: "Draft", value: "draft" },
];

export function SitemapBulkEditDialog({ count, pages, theme, saving, onClose, onApply, title = "Bulk Edit", intro, showTags = true, showStatus = true, showUrls = false, showSlug = false, initialSlug = "", initialRedirect = "", parentDisabled = false }: Props) {
  const [tagsInput, setTagsInput] = useState("");
  const [overwriteTags, setOverwriteTags] = useState(false);
  const [status, setStatus] = useState("");
  const [parent, setParent] = useState("");
  const [urlsInput, setUrlsInput] = useState("");
  const [slug, setSlug] = useState(initialSlug);
  const [redirect, setRedirect] = useState(initialRedirect);

  const lt = theme === "light";
  const tags = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
  const urlCount = urlsInput.split("\n").map(u => u.trim()).filter(Boolean).length;
  // Slug counts as a change only when shown, non-empty, and different from the current slug.
  const slugChanged = showSlug && slug.trim() !== "" && slug.trim() !== initialSlug.trim();
  // A redirect counts as a change when shown and different from the current redirect (adding, editing,
  // or clearing it all count).
  const redirectChanged = showSlug && redirect.trim() !== initialRedirect.trim();
  const fieldsSet = tags.length > 0 || status !== "" || parent !== "" || slugChanged || redirectChanged;
  // The custom-URLs variant also requires at least one pasted URL.
  const hasChanges = showUrls ? (urlCount > 0 && fieldsSet) : fieldsSet;
  const parentPages = [...pages].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));

  const panelBg = lt ? "#ffffff" : "#0f172a";
  const border = lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)";
  const textColor = lt ? "#0f172a" : "#e2e8f0";
  const labelColor = lt ? "#64748b" : "rgba(255,255,255,.6)";
  const inputBg = lt ? "#ffffff" : "rgba(255,255,255,.06)";
  const inputBorder = lt ? "1px solid #cbd5e1" : "1px solid rgba(255,255,255,.15)";

  const label: CSSProperties = { display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: labelColor, fontFamily: "'Inter','Mulish',sans-serif", marginBottom: 5 };
  const field: CSSProperties = { width: "100%", boxSizing: "border-box", padding: "9px 11px", background: inputBg, border: inputBorder, borderRadius: 6, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", color: textColor, outline: "none" };
  // Native <option> popups don't inherit the select's colors on all browsers — set them
  // explicitly so the dropdown list stays legible in dark mode.
  const optStyle: CSSProperties = { background: lt ? "#ffffff" : "#1e293b", color: lt ? "#0f172a" : "#e2e8f0" };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onMouseDown={() => { if (!saving) onClose(); }}
    >
      <div
        style={{ width: showUrls ? 900 : 640, maxWidth: "100%", background: panelBg, border, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.45)", padding: 22, fontFamily: "'Inter','Mulish',sans-serif" }}
        onMouseDown={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 17, fontWeight: 800, color: textColor, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: labelColor, marginBottom: 18, lineHeight: 1.5 }}>
          {intro ?? <>Applying to <strong style={{ color: textColor }}>{count}</strong> selected page{count === 1 ? "" : "s"}. Only the fields you set below are changed.</>}
        </div>

        {showUrls && (
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Paste URLs (one per line)</label>
          <textarea
            value={urlsInput}
            onChange={e => setUrlsInput(e.target.value)}
            placeholder={"https://example.com/page-one/\nhttps://example.com/page-two/\n/page-three/"}
            style={{ ...field, minHeight: 200, resize: "vertical", fontFamily: "'Roboto Mono', monospace", fontSize: 12, lineHeight: 1.5 }}
            disabled={saving}
            spellCheck={false}
          />
          <div style={{ fontSize: 11, color: labelColor, marginTop: 5 }}>
            {urlCount > 0 ? `${urlCount} URL${urlCount === 1 ? "" : "s"} entered. ` : ""}Each URL is matched to a page/post in the sitemap; the fields you set below are applied to every match.
          </div>
        </div>
        )}

        {showTags && (
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Add Tags (comma-separated)</label>
          <input
            value={tagsInput}
            onChange={e => setTagsInput(e.target.value)}
            placeholder="e.g. InnerPage, Service"
            style={field}
            disabled={saving}
          />
          <div style={{ fontSize: 11, color: labelColor, marginTop: 5 }}>
            {overwriteTags ? "Replaces each page's existing tags with the tags above." : "Added to each page's existing tags — duplicates are skipped."}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 9, cursor: saving ? "not-allowed" : "pointer" }}>
            <input type="checkbox" checked={overwriteTags} onChange={e => setOverwriteTags(e.target.checked)} disabled={saving} />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: textColor }}>Overwrite Existing Tags</span>
          </label>
        </div>
        )}

        {showStatus && (
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...field, cursor: saving ? "not-allowed" : "pointer" }} disabled={saving}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value} style={optStyle}>{o.label}</option>)}
          </select>
        </div>
        )}

        <div style={{ marginBottom: 22, opacity: parentDisabled ? 0.55 : 1 }}>
          <label style={label}>Parent Page</label>
          <ParentSelect
            value={parent}
            onChange={setParent}
            theme={theme}
            disabled={saving || parentDisabled}
            placeholder={parentDisabled ? "— Not applicable —" : "— No change —"}
            options={[
              { value: "", label: "— No change —" },
              { value: "0", label: "Top level (no parent)" },
              ...parentPages.map(p => ({ value: String(p.id), label: p.label })),
            ]}
          />
          <div style={{ fontSize: 11, color: labelColor, marginTop: 5 }}>
            {parentDisabled
              ? "Posts in WordPress do not have parent pages, so this doesn't apply."
              : "Only applies to pages (not posts). Changing the parent updates each page's URL; old→new redirects are created automatically."}
          </div>
        </div>

        {showSlug && (
        <div style={{ marginBottom: 22 }}>
          <label style={label}>URL Slug</label>
          <input
            value={slug}
            onChange={e => setSlug(e.target.value)}
            placeholder={initialSlug || "page-slug"}
            style={{ ...field, fontFamily: "'Roboto Mono', monospace", fontSize: 12.5 }}
            disabled={saving}
            spellCheck={false}
          />
          <div style={{ fontSize: 11, color: labelColor, marginTop: 5 }}>
            The last part of this page's URL. Changing it renames the page in WordPress; the old URL (and any child pages') 301-redirects to the new one automatically — the redirects are added to the Redirection Manager tab. WordPress lowercases the slug and replaces spaces/invalid characters with hyphens.
          </div>
        </div>
        )}

        {showSlug && (
        <div style={{ marginBottom: 22 }}>
          <label style={label}>Redirect Page (optional)</label>
          <input
            value={redirect}
            onChange={e => setRedirect(e.target.value)}
            placeholder="https://example.com/target-page/  or  /target-page/"
            style={{ ...field, fontFamily: "'Roboto Mono', monospace", fontSize: 12.5 }}
            disabled={saving}
            spellCheck={false}
          />
          <div style={{ fontSize: 11, color: labelColor, marginTop: 5 }}>
            Add a 301 redirect from this page's current URL to another URL. The redirect is created and added to the Redirection Manager tab, and this page's URL then shows in red in the sitemap.
          </div>
        </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: "9px 16px", background: lt ? "#fff" : "transparent", color: lt ? "#475569" : "#e2e8f0", border: inputBorder, borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
          >Cancel</button>
          <button
            onClick={() => onApply({ tags, overwriteTags, status, parent, urls: showUrls ? urlsInput : "", slug: showSlug ? slug.trim() : "", redirect: showSlug ? redirect.trim() : "", manageRedirect: showSlug })}
            disabled={saving || !hasChanges}
            style={{ padding: "9px 18px", background: (saving || !hasChanges) ? "#93c5a3" : "linear-gradient(135deg,#15803d,#16a34a)", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: (saving || !hasChanges) ? "not-allowed" : "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
          >{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}
